import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI, APIError, SESSION_EXPIRED_EVENT } from '../services/api';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'viewer';
  twoFactorEnabled: boolean;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  apiKey: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithApiKey: (apiKey: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function normalizeRole(role: string): User['role'] {
  const lower = role.toLowerCase();
  if (lower === 'admin' || lower === 'user' || lower === 'viewer') return lower;
  return 'viewer';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // apiKey is kept in memory ONLY (never persisted) — the primary auth is the
  // HTTP-only session cookie (JWT), which survives reloads XSS-safely.
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount, restore the session from the HTTP-only session cookie.
  useEffect(() => {
    let cancelled = false;

    authAPI.getSession()
      .then((profile) => {
        if (!cancelled && profile) {
          setUser({
            id: profile.id.toString(),
            name: profile.username,
            email: profile.email,
            role: normalizeRole(profile.role),
            twoFactorEnabled: false,
            avatar: undefined,
          });
        }
      })
      .catch(() => {
        // No valid session cookie — user needs to log in
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password);
      // Session cookie is set by the server; keep the key in memory only
      setApiKey(response.api_key);
      setUser({
        id: response.user.id.toString(),
        name: response.user.username,
        email: response.user.email,
        role: normalizeRole(response.user.role),
        twoFactorEnabled: false,
        avatar: undefined,
      });
    } catch (error) {
      if (error instanceof APIError) {
        throw new Error(error.message);
      }
      throw new Error('Login failed. Please try again.');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // Even if logout API fails, clear local state
    }
    setUser(null);
    setApiKey(null);
  }, []);

  const loginWithApiKey = useCallback(async (key: string) => {
    try {
      // Server validates the key and sets the HTTP-only session cookie (JWT).
      // The key itself is never persisted in the browser (XSS-safe).
      const response = await authAPI.loginWithApiKey(key);
      setApiKey(null);
      setUser({
        id: response.user.id.toString(),
        name: response.user.username,
        email: response.user.email,
        role: normalizeRole(response.user.role),
        twoFactorEnabled: false,
        avatar: undefined,
      });
    } catch (error) {
      if (error instanceof APIError) {
        throw new Error(error.message);
      }
      throw new Error('Invalid API key. Please check and try again.');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await authAPI.getSession();
      if (profile) {
        setUser({
          id: profile.id.toString(),
          name: profile.username,
          email: profile.email,
          role: normalizeRole(profile.role),
          twoFactorEnabled: false,
          avatar: undefined,
        });
      }
    } catch {
      // If cookie-based session check fails, try API key
      if (apiKey) {
        try {
          const profile = await authAPI.getProfile(apiKey);
          setUser({
            id: profile.id.toString(),
            name: profile.username,
            email: profile.email,
            role: normalizeRole(profile.role),
            twoFactorEnabled: false,
            avatar: undefined,
          });
        } catch {
          // Session expired
        }
      }
    }
  }, [apiKey]);

  // Sliding session renewal — re-issue the JWT cookie periodically so
  // active users don't get logged out after the fixed 24h expiry.
  useEffect(() => {
    if (!user) return;

    const renew = () => {
      authAPI.refreshSession()
        .then(() => {})
        .catch(() => {
          // Token expired — the next 401 auto-heal in apiCall will handle
          // logout via the session-expired event.
        });
    };

    const timer = setInterval(renew, 20 * 60 * 1000); // every 20 minutes

    // Renew immediately when the user returns to the tab. Background-tab
    // timers are throttled/paused by browsers, so the cookie can expire
    // while the page sits stale — refocusing is the reliable moment to
    // slide the session forward again.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') renew();
    };
    const onFocus = () => renew();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [user]);

  // Global logout when a 401 could not be healed (session genuinely gone).
  useEffect(() => {
    const onSessionExpired = () => { setUser(null); setApiKey(null); };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const contextValue = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    apiKey,
    login,
    loginWithApiKey,
    logout,
    refreshUser,
  }), [user, isLoading, apiKey, login, loginWithApiKey, logout, refreshUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
